import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import {
  FileText, BookOpen, Coins, Map, Shield, Lock,
  AlertTriangle, Palette, HelpCircle, ExternalLink, Download
} from 'lucide-react';

const documents = [
  { title: 'Whitepaper', description: 'Complete technical and economic overview of the $GFG ecosystem. v4.0 Seasons Edition', icon: BookOpen, file: '/docs/whitepaper.pdf', color: '#b4883c', featured: true },
  { title: 'Litepaper', description: 'Quick 2-minute overview of Gas-Free Gremlins', icon: FileText, file: '/docs/litepaper.pdf', color: '#c4956a' },
  { title: 'Tokenomics', description: 'Token distribution, seasons economy, burn mechanics, and dual currency model. v5.0', icon: Coins, file: '/docs/tokenomics.pdf', color: '#9a7b4f' },
  { title: 'Roadmap', description: 'Project milestones from seasons launch through 2026+', icon: Map, file: '/docs/roadmap.pdf', color: '#7c9070' },
  { title: 'Terms of Service', description: 'Legal terms governing platform usage and arcade participation', icon: Shield, file: '/docs/terms-of-service.pdf', color: '#8a8070' },
  { title: 'Privacy Policy', description: 'How we collect, use, and protect your data including wallet info', icon: Lock, file: '/docs/privacy-policy.pdf', color: '#8a8070' },
  { title: 'Risk Disclosure', description: 'Important risks associated with cryptocurrency participation', icon: AlertTriangle, file: '/docs/risk-disclosure.pdf', color: '#b8864a' },
  { title: 'Brand Guidelines', description: 'Visual identity, voice, messaging framework, and asset usage', icon: Palette, file: '/docs/brand-guidelines.pdf', color: '#9a7b4f' },
  { title: 'FAQ', description: 'Frequently asked questions about $GFG, seasons, arcade, and more', icon: HelpCircle, file: '/docs/faq.pdf', color: '#7c9070' },
];

// Panel — beige/parchment theme
const Panel = ({ children, className = '', glow = false, style = {}, ...props }) => (
  <div className={`relative overflow-hidden ${className}`} {...props}
    style={{
      background: glow ? 'rgba(20,16,10,0.85)' : 'rgba(16,13,8,0.8)',
      border: `1px solid rgba(180,136,60,${glow ? '0.3' : '0.12'})`,
      backdropFilter: 'blur(10px)',
      boxShadow: glow ? '0 0 20px rgba(180,136,60,0.06), inset 0 0 15px rgba(180,136,60,0.02)' : 'inset 0 0 10px rgba(180,136,60,0.01)',
      ...style,
    }}>
    <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l z-10" style={{ borderColor: 'rgba(180,136,60,0.3)' }} />
    <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r z-10" style={{ borderColor: 'rgba(180,136,60,0.3)' }} />
    <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l z-10" style={{ borderColor: 'rgba(180,136,60,0.3)' }} />
    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r z-10" style={{ borderColor: 'rgba(180,136,60,0.3)' }} />
    <motion.div className="absolute inset-0 pointer-events-none"
      style={{ background: 'linear-gradient(135deg, transparent 40%, rgba(180,136,60,0.03) 50%, transparent 60%)', backgroundSize: '300% 300%' }}
      animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'linear' }} />
    {children}
  </div>
);

export default function DocsPage() {
  const navigate = useNavigate();

  const handleDownload = (file, title) => {
    const link = document.createElement('a');
    link.href = file;
    link.download = `GFG-${title.toLowerCase().replace(/\s+/g, '-')}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleView = (file) => window.open(file, '_blank');

  return (
    <div className="min-h-screen text-white relative" data-testid="docs-page" style={{ background: '#0c0a06' }}>

      {/* Background */}
      <div className="fixed inset-0 z-0" style={{ background: 'radial-gradient(ellipse at 50% 20%, #16120a 0%, #100d07 40%, #0c0a06 100%)' }} />
      <div className="fixed inset-0 z-[1] pointer-events-none">
        <motion.div className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(180,136,60,0.05) 0%, transparent 50%)' }}
          animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 4, repeat: Infinity }} />
      </div>
      {/* CRT */}
      <div className="fixed inset-0 z-[60] pointer-events-none mix-blend-overlay" style={{
        background: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.15) 50%)', backgroundSize: '100% 4px', opacity: 0.08,
      }} />
      {/* Dust motes */}
      <div className="fixed inset-0 z-[2] pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <motion.div key={`d-${i}`} className="absolute rounded-full"
            style={{ left: `${8 + i * 11}%`, bottom: '-8px', width: '2px', height: '2px',
              background: '#b4883c', boxShadow: '0 0 4px rgba(180,136,60,0.5)' }}
            animate={{ y: [0, -500], opacity: [0, 0.4, 0.1, 0] }}
            transition={{ duration: 8 + Math.random() * 4, delay: i * 0.8, repeat: Infinity, ease: 'easeOut' }}
          />
        ))}
      </div>

      <BackButton to="/" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 pt-20 pb-16">

        {/* Header */}
        <div className="mb-10">
          <div className="relative">
            <div className="absolute -inset-x-6 -inset-y-3 pointer-events-none" style={{
              background: 'radial-gradient(ellipse at 40% 60%, rgba(180,136,60,0.06) 0%, transparent 60%)', filter: 'blur(12px)',
            }} />
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] uppercase tracking-[0.5em] mb-1 relative"
              style={{ fontFamily: "'Cinzel', serif", color: 'rgba(180,136,60,0.55)' }}>Ancient Knowledge of the Gremlins</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
              className="relative text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider inline-block"
              style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }} data-testid="docs-title">
              <span className="absolute inset-0" style={{ color: 'transparent', WebkitTextStroke: '2px rgba(180,136,60,0.15)', filter: 'blur(6px)' }}>THE ARCHIVES</span>
              <span className="relative" style={{
                background: 'linear-gradient(180deg, #f5e6c8 0%, #d4a85c 15%, #b4883c 35%, #9a7b4f 55%, #7c6638 75%, #5c4a28 95%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 20px rgba(180,136,60,0.3)) drop-shadow(0 0 40px rgba(180,136,60,0.1))',
              }}>THE ARCHIVES</span>
              <motion.span className="absolute inset-0 pointer-events-none"
                style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(245,230,200,0.3) 50%, transparent 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundSize: '200% 100%' }}
                animate={{ backgroundPosition: ['200% 0%', '-200% 0%'] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}>THE ARCHIVES</motion.span>
            </motion.h1>
          </div>
          <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-3 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(180,136,60,0.3), rgba(180,136,60,0.15), transparent)' }} />
          <p className="text-xs mt-3" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(180,136,60,0.35)' }}>
            Sacred scrolls containing everything you need to know about Gas-Free Gremlins
          </p>
        </div>

        {/* Featured: Whitepaper */}
        {documents.filter(d => d.featured).map(doc => (
          <Panel glow key={doc.title} className="p-4 md:p-6 mb-6 md:mb-8" data-testid="featured-doc">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-3 md:gap-4 relative z-10">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="p-2 md:p-3 flex-shrink-0" style={{ background: `${doc.color}15`, border: `1px solid ${doc.color}30` }}>
                  <doc.icon className="w-5 h-5 md:w-6 md:h-6" style={{ color: doc.color, filter: `drop-shadow(0 0 4px ${doc.color}50)` }} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-base md:text-lg font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(245,230,200,0.85)' }}>{doc.title}</h2>
                    <span className="text-[8px] md:text-[9px] px-1.5 md:px-2 py-0.5 font-bold tracking-wider uppercase" style={{ fontFamily: "'Cinzel', serif", background: `${doc.color}18`, color: doc.color, border: `1px solid ${doc.color}30` }}>Featured</span>
                  </div>
                  <p className="text-[10px] md:text-xs" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(180,136,60,0.5)' }}>{doc.description}</p>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
                <button onClick={() => handleView(doc.file)}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 md:px-5 py-2 text-xs font-bold tracking-wider uppercase transition-all hover:scale-105"
                  style={{ fontFamily: "'Cinzel', serif", background: `${doc.color}18`, border: `1px solid ${doc.color}35`, color: doc.color }}
                  data-testid="whitepaper-view-btn">
                  <ExternalLink className="w-3.5 h-3.5" /> Read
                </button>
                <button onClick={() => handleDownload(doc.file, doc.title)}
                  className="flex items-center gap-2 px-3 md:px-4 py-2 text-xs transition-all hover:scale-105"
                  style={{ background: 'rgba(180,136,60,0.05)', border: '1px solid rgba(180,136,60,0.12)', color: 'rgba(180,136,60,0.5)' }}>
                  <Download className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </Panel>
        ))}

        {/* Document Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 mb-8 md:mb-10">
          {documents.filter(d => !d.featured).map((doc, i) => (
            <motion.div key={doc.title} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.05 }}>
              <Panel className="p-5 h-full" data-testid={`doc-${doc.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-start gap-4 relative z-10">
                  <div className="p-2 flex-shrink-0" style={{ background: `${doc.color}12`, border: `1px solid ${doc.color}25` }}>
                    <doc.icon className="w-4 h-4" style={{ color: doc.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold mb-1" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(245,230,200,0.8)' }}>{doc.title}</h3>
                    <p className="text-[10px] mb-3 line-clamp-2" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(180,136,60,0.4)' }}>{doc.description}</p>
                    <div className="flex gap-3">
                      <button onClick={() => handleView(doc.file)}
                        className="flex items-center gap-1.5 text-[10px] font-bold tracking-wider uppercase transition-all hover:scale-105"
                        style={{ fontFamily: "'Cinzel', serif", color: doc.color }}>
                        <ExternalLink className="w-3 h-3" /> View
                      </button>
                      <button onClick={() => handleDownload(doc.file, doc.title)}
                        className="flex items-center gap-1.5 text-[10px] font-bold tracking-wider uppercase transition-all hover:scale-105"
                        style={{ fontFamily: "'Cinzel', serif", color: 'rgba(180,136,60,0.35)' }}>
                        <Download className="w-3 h-3" /> Download
                      </button>
                    </div>
                  </div>
                </div>
              </Panel>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-[9px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(180,136,60,0.2)' }}>
            All documents are provided in PDF format for easy reading and professional sharing.
          </p>
        </div>
      </div>
    </div>
  );
}
